import { z } from "zod";
import { MESSAGE_STATES } from "../communication/contracts";
import { AI_OBJECTIVES } from "../ai-messages/contracts";
import {
  DEFAULT_FLOW_LOOP_LIMIT,
  DEFAULT_FLOW_REPLY_POLICY,
  DEFAULT_FLOW_REENTRY_POLICY,
  DEFAULT_FUNNEL_STRUCTURE_TYPE,
  FLOW_BASE_FIELDS,
  FLOW_BILLING_CHANNELS,
  FLOW_CALL_ORIGIN_POLICIES,
  FLOW_CALL_SEMANTICS,
  FLOW_CONDITION_OPERATORS,
  FLOW_DOMAIN_EVENT_TRIGGERS,
  FLOW_DRAFT_STATUSES,
  FLOW_EDGE_PORTS,
  FLOW_ENTRY_POLICIES,
  FLOW_EXECUTION_MODES,
  FLOW_LOOP_LIMIT_MAX,
  FLOW_LOOP_LIMIT_MIN,
  FLOW_MEETING_PARTICIPANTS,
  FLOW_MESSAGE_EVENTS,
  FLOW_NODE_TYPES,
  FLOW_REENTRY_POLICIES,
  FLOW_REPLY_POLICIES,
  FLOW_TIME_EVENTS,
  FLOW_VALUE_TYPES,
  FUNNEL_STRUCTURE_TYPES,
  type FlowActiveVersion,
  type FlowCondition,
  type FlowDraft,
  type FlowEdge,
  type FlowExecutionMode,
  type FlowNode,
  type FlowReplyPolicy,
  type FlowVersion,
  type FunnelStructureType,
} from "./contracts";
import { buildFlowGraphIndex, canonicalEdgeOrder, canonicalNodeOrder, cycleClosingEdgeIds } from "./graph";

/**
 * Schemas e normalização da definição de flows.
 *
 * Nenhuma validação estrutural de grafo vive aqui: o schema garante FORMA
 * (tipos, enums, limites numéricos) e a normalização aplica defaults seguros.
 * Alcançabilidade, ciclos, portas e dependências pertencem ao validador.
 */

/** Versão do estado local persistido. Dados antigos são migrados por default. */
export const FLOW_STATE_SCHEMA_VERSION = 2;

const identifier = z.string().trim().min(1).max(120);
const shortText = z.string().trim().max(160);
const longText = z.string().trim().max(2000);

export const funnelStructureTypeSchema = z.enum(FUNNEL_STRUCTURE_TYPES);
export const flowExecutionModeSchema = z.enum(FLOW_EXECUTION_MODES);
export const flowEntryPolicySchema = z.enum(FLOW_ENTRY_POLICIES);
export const flowDomainEventTriggerSchema = z.enum(FLOW_DOMAIN_EVENT_TRIGGERS);
export const flowReentryPolicySchema = z.enum(FLOW_REENTRY_POLICIES);
export const flowReplyPolicySchema = z.enum(FLOW_REPLY_POLICIES);
export const flowNodeTypeSchema = z.enum(FLOW_NODE_TYPES);
export const flowEdgePortSchema = z.enum(FLOW_EDGE_PORTS);

/** Evento de mensagem: subconjunto explícito dos estados já versionados. */
export const flowMessageEventSchema = z
  .enum(MESSAGE_STATES)
  .refine((event) => FLOW_MESSAGE_EVENTS.includes(event), {
    message: "Evento de mensagem fora da allowlist do domínio de flows.",
  });

export const flowConditionOperandSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("base_field"), field: z.enum(FLOW_BASE_FIELDS) }),
  z.object({
    kind: z.literal("custom_field"),
    fieldId: identifier,
    valueType: z.enum(FLOW_VALUE_TYPES),
  }),
  z.object({ kind: z.literal("tag") }),
  z.object({ kind: z.literal("trigger") }),
  z.object({ kind: z.literal("stage") }),
  z.object({ kind: z.literal("membership_status") }),
  z.object({
    kind: z.literal("reply_classification"),
    catalogVersionId: identifier,
    catalogVersion: z.number().int().positive(),
  }),
  z.object({
    kind: z.literal("approach_type"),
    catalogVersionId: identifier,
    catalogVersion: z.number().int().positive(),
  }),
  z.object({ kind: z.literal("message_event"), event: flowMessageEventSchema }),
  z.object({ kind: z.literal("task_completed"), taskNodeId: identifier }),
  z.object({ kind: z.literal("time_since_event"), event: z.enum(FLOW_TIME_EVENTS) }),
  z.object({ kind: z.literal("time_in_stage") }),
  z.object({ kind: z.literal("related_membership"), funnelId: identifier }),
]);

export const flowConditionLiteralSchema = z.union([
  z.string().max(500),
  z.number().finite(),
  z.boolean(),
  z.array(z.union([z.string().max(500), z.number().finite()])).min(1).max(50),
]);

/**
 * Expressão declarativa. Não há string de código, SQL, template ou operador
 * definido pelo usuário: apenas comparação tipada e composição booleana.
 */
export const flowConditionSchema: z.ZodType<FlowCondition> = z.lazy(() =>
  z.discriminatedUnion("type", [
    z.object({
      type: z.literal("comparison"),
      operand: flowConditionOperandSchema,
      operator: z.enum(FLOW_CONDITION_OPERATORS),
      value: flowConditionLiteralSchema.optional(),
    }),
    z.object({ type: z.literal("all"), conditions: z.array(flowConditionSchema).min(1).max(20) }),
    z.object({ type: z.literal("any"), conditions: z.array(flowConditionSchema).min(1).max(20) }),
    z.object({ type: z.literal("not"), condition: flowConditionSchema }),
  ]),
);

export const flowSplitVariantSchema = z.object({
  key: z.string().trim().regex(/^[a-z][a-z0-9_]*$/, "Chave da variante inválida."),
  label: shortText.min(1),
  weight: z.number().int().min(0).max(100),
});

export const flowFieldAssignmentSchema = z.object({
  target: z.enum(["lead_field", "tag", "trigger"]),
  id: identifier,
  operation: z.enum(["set", "clear", "add", "remove"]),
  value: z.union([z.string().max(500), z.number().finite(), z.boolean(), z.null()]).optional(),
});

export const flowCallTargetSchema = z.discriminatedUnion("scope", [
  z.object({
    scope: z.literal("same_funnel"),
    flowId: identifier,
    flowVersionId: identifier,
    flowVersion: z.number().int().positive(),
    semantics: z.enum(FLOW_CALL_SEMANTICS),
  }),
  z.object({
    scope: z.literal("other_funnel"),
    funnelId: identifier,
    flowId: identifier,
    flowVersionId: identifier,
    flowVersion: z.number().int().positive(),
    originPolicy: z.enum(FLOW_CALL_ORIGIN_POLICIES),
  }),
]);

export const flowNodeConfigSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("start") }),
  z.object({ type: z.literal("condition") }),
  z.object({
    type: z.literal("random_split"),
    variants: z.array(flowSplitVariantSchema).min(2).max(10),
  }),
  z.object({ type: z.literal("stage"), stageId: identifier }),
  // Intervalo mínimo antes da próxima ação; o runner futuro é quem espera.
  z.object({ type: z.literal("wait"), durationMinutes: z.number().int().min(1).max(525600) }),
  z.object({
    type: z.literal("human_task"),
    instruction: longText.min(1),
    taskActionType: z.enum(["human_task", "review", "sensitive_action"]),
    sensitive: z.boolean(),
  }),
  z.object({
    type: z.literal("send_message"),
    channel: z.literal("email"),
    templateId: identifier,
    templateVersionId: identifier,
    templateVersion: z.number().int().positive(),
  }),
  z.object({
    type: z.literal("meeting"),
    participants: z.enum(FLOW_MEETING_PARTICIPANTS),
  }),
  z.object({
    type: z.literal("billing"),
    // Dias antes do vencimento, do mais distante ao dia do vencimento.
    offsetsDays: z.array(z.number().int().min(0).max(120)).min(1).max(6),
    channels: z.array(z.enum(FLOW_BILLING_CHANNELS)).min(1).max(3),
  }),
  z.object({
    type: z.literal("ai_response"),
    objective: z.enum(AI_OBJECTIVES),
    promptVersionId: identifier,
    promptVersion: z.number().int().positive(),
  }),
  z.object({
    type: z.literal("set_field_or_tag"),
    assignments: z.array(flowFieldAssignmentSchema).min(1).max(20),
  }),
  z.object({ type: z.literal("call_flow"), target: flowCallTargetSchema }),
  z.object({ type: z.literal("won") }),
  // Perda exige motivo, etapa e evidência — invariante do domínio (AGENTS §7.2).
  z.object({
    type: z.literal("lost"),
    lossReasonId: identifier,
    stageId: identifier,
    evidence: longText.min(1),
  }),
  z.object({ type: z.literal("end"), outcomeNote: longText.optional() }),
]);

export const flowNodePositionSchema = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
});

export const flowNodeSchema = z
  .object({
    id: identifier,
    type: flowNodeTypeSchema,
    label: shortText.min(1),
    config: flowNodeConfigSchema,
    stageId: identifier.optional(),
    note: longText.optional(),
    mode: flowExecutionModeSchema.optional(),
    position: flowNodePositionSchema.optional(),
  })
  .refine((node) => node.config.type === node.type, {
    message: "A configuração do nó precisa ter o mesmo tipo do nó.",
    path: ["config"],
  });

export const flowEdgeSchema = z.object({
  id: identifier,
  fromNodeId: identifier,
  fromPort: flowEdgePortSchema,
  toNodeId: identifier,
  priority: z.number().int().min(0).max(1000),
  label: shortText.optional(),
  condition: flowConditionSchema.optional(),
  fallback: z.boolean(),
  variantKey: z.string().trim().min(1).max(60).optional(),
  loopLimit: z.number().int().min(FLOW_LOOP_LIMIT_MIN).max(FLOW_LOOP_LIMIT_MAX).optional(),
});

export const flowDraftBodySchema = z.object({
  name: shortText.min(1),
  description: longText.default(""),
  structureType: funnelStructureTypeSchema,
  mode: flowExecutionModeSchema.optional(),
  entryPolicy: flowEntryPolicySchema,
  entryDomainEvent: flowDomainEventTriggerSchema.optional(),
  // Default seguro: com execução ativa futura, nova inscrição é ignorada.
  reentryPolicy: flowReentryPolicySchema.default(DEFAULT_FLOW_REENTRY_POLICY),
  // Política de resposta é decisão obrigatória e não recebe default aqui.
  replyPolicy: flowReplyPolicySchema,
  isEntryFlow: z.boolean(),
  nodes: z.array(flowNodeSchema).min(1).max(300),
  edges: z.array(flowEdgeSchema).max(600),
});

export const flowDraftSchema = flowDraftBodySchema.extend({
  id: identifier,
  ownerId: identifier,
  funnelId: identifier,
  revision: z.number().int().positive(),
  status: z.enum(FLOW_DRAFT_STATUSES),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
  archivedAt: z.string().datetime({ offset: true }).optional(),
});

export type FlowDraftBodyInput = z.input<typeof flowDraftBodySchema>;

// ---------------------------------------------------------------------------
// Normalização de dados legados e defaults seguros
// ---------------------------------------------------------------------------

/**
 * Funil sem decisão persistida é `simple`. Nada é reinterpretado: etapas,
 * visitas, eventos, ordenação e analytics continuam exatamente como estavam.
 */
export function normalizeFunnelStructureType(value: unknown): FunnelStructureType {
  const parsed = funnelStructureTypeSchema.safeParse(value);
  return parsed.success ? parsed.data : DEFAULT_FUNNEL_STRUCTURE_TYPE;
}

/** Modo do funil/flow: ausência significa "sem decisão", não `automatic`. */
export function normalizeFlowExecutionMode(value: unknown): FlowExecutionMode | undefined {
  const parsed = flowExecutionModeSchema.safeParse(value);
  return parsed.success ? parsed.data : undefined;
}

export function normalizeFlowReplyPolicy(value: unknown): FlowReplyPolicy {
  const parsed = flowReplyPolicySchema.safeParse(value);
  // Decisão confirmada: sem regra persistida, continuar e notificar. O publish
  // futuro ainda precisa tornar esse risco visível e exigir confirmação.
  return parsed.success ? parsed.data : DEFAULT_FLOW_REPLY_POLICY;
}

/**
 * Preenche o limite padrão nas arestas que fecham ciclo e ainda não declaram
 * um. Isso não substitui a validação: limite fora de 1–100 continua sendo erro.
 */
export function applyDefaultLoopLimits(nodes: FlowNode[], edges: FlowEdge[], startNodeId?: string): FlowEdge[] {
  const start = startNodeId ?? nodes.find((node) => node.type === "start")?.id;
  if (!start) return edges;
  const index = buildFlowGraphIndex(nodes, edges);
  const closing = cycleClosingEdgeIds(index, start);
  return edges.map((edge) =>
    closing.has(edge.id) && edge.loopLimit === undefined
      ? { ...edge, loopLimit: DEFAULT_FLOW_LOOP_LIMIT }
      : edge,
  );
}

export interface NormalizeFlowDraftInput extends FlowDraftBodyInput {
  id: string;
  ownerId: string;
  funnelId: string;
  revision: number;
  status?: FlowDraft["status"];
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
}

/**
 * Normalização determinística de um rascunho: valida a forma, aplica defaults
 * seguros, ordena nós/arestas canonicamente e preenche o limite de loop padrão.
 */
export function normalizeFlowDraft(input: NormalizeFlowDraftInput): FlowDraft {
  const body = flowDraftBodySchema.parse(input);
  const nodes = canonicalNodeOrder(body.nodes as FlowNode[]);
  const edges = canonicalEdgeOrder(
    applyDefaultLoopLimits(nodes, body.edges as FlowEdge[]),
  );
  return {
    id: input.id,
    ownerId: input.ownerId,
    funnelId: input.funnelId,
    structureType: body.structureType,
    name: body.name,
    description: body.description,
    revision: input.revision,
    mode: body.mode,
    entryPolicy: body.entryPolicy,
    entryDomainEvent: body.entryPolicy === "domain_event" ? body.entryDomainEvent : undefined,
    reentryPolicy: body.reentryPolicy,
    replyPolicy: body.replyPolicy,
    isEntryFlow: body.isEntryFlow,
    nodes,
    edges,
    status: input.status ?? "editable",
    createdAt: input.createdAt,
    updatedAt: input.updatedAt,
    archivedAt: input.archivedAt,
  };
}

/**
 * Migração do estado local: rascunho antigo, sem campos novos, recebe defaults
 * seguros em vez de sumir da tela. Rascunho irrecuperável é descartado com
 * registro, nunca convertido em objeto parcialmente inválido.
 */
export function normalizeStoredFlowDrafts(
  drafts: readonly unknown[] | undefined,
  ownerId: string,
): FlowDraft[] {
  if (!drafts?.length) return [];
  const normalized: FlowDraft[] = [];
  for (const raw of drafts) {
    if (!raw || typeof raw !== "object") continue;
    const candidate = raw as Record<string, unknown>;
    const parsed = flowDraftSchema.safeParse({
      ...candidate,
      ownerId: typeof candidate.ownerId === "string" && candidate.ownerId ? candidate.ownerId : ownerId,
      description: typeof candidate.description === "string" ? candidate.description : "",
      structureType: normalizeFunnelStructureType(candidate.structureType),
      reentryPolicy: candidate.reentryPolicy ?? DEFAULT_FLOW_REENTRY_POLICY,
      replyPolicy: normalizeFlowReplyPolicy(candidate.replyPolicy),
      isEntryFlow: candidate.isEntryFlow === true,
      status: candidate.status === "archived" ? "archived" : "editable",
      revision: typeof candidate.revision === "number" && candidate.revision >= 1 ? candidate.revision : 1,
      nodes: Array.isArray(candidate.nodes) ? candidate.nodes : [],
      edges: Array.isArray(candidate.edges) ? candidate.edges : [],
    });
    if (!parsed.success) continue;
    normalized.push({ ...parsed.data, ownerId });
  }
  return normalized;
}

export const flowActiveVersionSchema: z.ZodType<FlowActiveVersion> = z.object({
  ownerId: identifier,
  flowId: identifier,
  funnelId: identifier,
  flowVersionId: identifier,
  version: z.number().int().positive(),
  activatedAt: z.string().datetime({ offset: true }),
  pausedAt: z.string().datetime({ offset: true }).optional(),
});

/**
 * Migração aditiva da referência ativa. Entradas legadas ausentes viram lista
 * vazia; entradas corrompidas são descartadas em vez de apontarem novas runs
 * para uma versão ambígua.
 */
export function normalizeStoredFlowActiveVersions(
  activeVersions: readonly unknown[] | undefined,
  ownerId: string,
  publishedVersions: readonly FlowVersion[],
): FlowActiveVersion[] {
  if (!activeVersions?.length) return [];
  const parsed = activeVersions.flatMap((raw) => {
    if (!raw || typeof raw !== "object") return [];
    const result = flowActiveVersionSchema.safeParse({
      ...(raw as Record<string, unknown>),
      ownerId,
    });
    if (!result.success) return [];
    const reference = result.data;
    const target = publishedVersions.find(
      (version) =>
        version.ownerId === ownerId &&
        version.id === reference.flowVersionId &&
        version.flowId === reference.flowId &&
        version.funnelId === reference.funnelId &&
        version.version === reference.version,
    );
    return target ? [{ reference, target }] : [];
  });

  // Espelha a PK (owner, flow): estado local repetido fica com a ativação mais
  // recente, nunca com duas respostas possíveis para novas inscrições.
  const byFlow = new Map<string, (typeof parsed)[number]>();
  for (const item of parsed) {
    const key = `${ownerId}:${item.reference.flowId}`;
    const current = byFlow.get(key);
    if (
      !current ||
      Date.parse(item.reference.activatedAt) >= Date.parse(current.reference.activatedAt)
    )
      byFlow.set(key, item);
  }

  // Um funil graph possui no máximo um entry flow ativo. Se um estado legado
  // trouxer dois, só a ativação mais recente sobrevive; as versões publicadas
  // continuam intactas e auditáveis.
  const latestEntryByFunnel = new Map<string, (typeof parsed)[number]>();
  for (const item of byFlow.values()) {
    if (!item.target.snapshot.isEntryFlow) continue;
    const current = latestEntryByFunnel.get(item.reference.funnelId);
    if (
      !current ||
      Date.parse(item.reference.activatedAt) >= Date.parse(current.reference.activatedAt)
    )
      latestEntryByFunnel.set(item.reference.funnelId, item);
  }

  return [...byFlow.values()]
    .filter(
      (item) =>
        !item.target.snapshot.isEntryFlow ||
        latestEntryByFunnel.get(item.reference.funnelId) === item,
    )
    .map((item) => item.reference);
}
